<template>
  <div class="order-status-container-wrapper">
    <div v-if="$route.name === routesConst.ORDER_IN_PROGRESS" class="order-status-container order-in-progress d-flex flex-column justify-content-center align-items-center">
      <span class="loader"></span>
      <div class="countDown">{{ countDownOrderInProgress }} s.</div>
      <div class="order-in-progress__title text-center">{{ $t('order_in_progress') }}</div>
      <div class="order-in-progress__text">{{ $t('please_wait') }}</div>
    </div>

    <div v-if="$route.name === routesConst.ORDER_COMPLETED" class="order-status-container order-completed d-flex flex-column justify-content-center align-items-center">
      <span class="icon"><img src="/assets/images/check-circle-success.png" alt=""></span>
      <div class="order-in-progress__title text-center">{{ $t('order_payment_successful') }}</div>
      <div class="order-in-progress__text">{{ $t('order_info_sent_to_email') }}</div>
      <div class="countDown">{{$t('redirecting')}} {{ countDownOrderCompleted }} s.</div>
      <ButtonComponent class="order-status-form-button" v-on:click.prevent="redirectToCustomerPurchaseHistory">
        <template #btnContent>{{ $t('next') }}</template>
      </ButtonComponent>
    </div>

    <div v-if="$route.name === routesConst.ORDER_FAILED" class="order-status-container order-failed d-flex flex-column justify-content-center align-items-center">
      <span class="icon"><img src="/assets/images/times-failed.png" alt=""></span>
      <div class="order-in-progress__title text-center">{{ $t('order_progress_failed') }}</div>
      <div class="order-in-progress__text">{{ $t('please_try_again') }}</div>
      <router-link :to="'/' + routesConst.ORDER">
        <ButtonComponent class="order-status-form-button">
          <template #btnContent>{{ $t('try_again') }}</template>
        </ButtonComponent>
      </router-link>
    </div>

    <div v-if="$route.name === routesConst.ORDER_REGISTERED" class="order-status-container order-completed d-flex flex-column justify-content-center align-items-center">
      <span class="icon"><img src="/assets/images/check-circle-success.png" alt=""></span>
      <div class="order-in-progress__title text-center">{{ $t('order_registered') }}</div>
      <div class="order-in-progress__text">{{ $t('order_info_sent_to_email') }}</div>
      <div class="countDown">{{$t('redirecting')}} {{ countDownOrderCompleted }} s.</div>
      <ButtonComponent class="order-status-form-button" v-on:click.prevent="redirectToCustomerPurchaseHistory">
        <template #btnContent>{{ $t('next') }}</template>
      </ButtonComponent>
    </div>
  </div>
</template>

<script lang="ts">
import './orderProgress.scss';
import {defineComponent} from "vue";
//@ts-ignore
import ButtonComponent from '../../components/Button/ButtonComponent.vue';
import axios from "axios";
//@ts-ignore
import apiService from "../../services/apiService";
import {routesConst} from '../../routes.js';
import {useStore} from "vuex";
import {PaymentStatuses} from "../../interfaces/enums/PaymentStatuses.enum";
import RentService from "../../services/rentService";

export default defineComponent({
  name: 'OrderProgressPage',
  components: {ButtonComponent},
  props: {},
  setup() {
    const store = useStore();
    return {
      store
    };
  },
  data() {
    return {
      routesConst: routesConst,
      hearbeat: {
        interval: 0, // setInterval() is stored here
        timesLooped: 0,
        timeout: 30000,
        intervalTimeout: 5000,
        loading: false
      },
      completedRedirectTimeout: 5000,

      countDownId: 0, // setTimeout() is stored here
      countDownOrderCompleted: 5,
      countDownOrderInProgress: 30,
    }
  },

  methods: {
    countDownTimer(): void {

      if ([
          this.routesConst.ORDER_COMPLETED,
          this.routesConst.ORDER_REGISTERED
        ].includes(this.$route.name)
      ) {
        clearTimeout(this.countDownId);

        if (this.countDownOrderCompleted > 0) {
          this.countDownId = setTimeout(() => {
            this.countDownOrderCompleted--;
            this.countDownTimer();
          }, 1000);
        }
      } else {
        clearTimeout(this.countDownId);

        if (this.countDownOrderInProgress > 0) {
          this.countDownId = setTimeout(() => {
            this.countDownOrderInProgress--;
            this.countDownTimer();
          }, 1000);
        }
      }

    },

    init(): void {
      switch (this.$route.name) {
        case routesConst.ORDER_IN_PROGRESS:
          this.clearOrderStoreAndLocalStorage();
          this.handleOrderInProgressPage();

          break;

        case routesConst.ORDER_COMPLETED:
          this.clearOrderStoreAndLocalStorage();
          this.handleOrderCompletedPage();

          break;

        case routesConst.ORDER_FAILED:
          // Let user go back to order on click
          break;

        case routesConst.ORDER_REGISTERED:
          this.clearOrderStoreAndLocalStorage();
          this.handleOrderRegisteredPage();
          break;
      }
    },

    handleOrderCompletedPage(): void {
      const _this = this;

      // Redirect in 5 seconds
      this.countDownTimer();

      setTimeout(() => {
        this.redirectToCustomerPurchaseHistory();
      }, this.completedRedirectTimeout);
    },

    handleOrderRegisteredPage(): void {
      const _this = this;

      // Redirect in 5 seconds
      this.countDownTimer();

      setTimeout(() => {
        this.redirectToCustomerPurchaseHistory();
      }, this.completedRedirectTimeout);
    },

    handleOrderInProgressPage(): void {
      const _this = this;

      startHearbeat();

      function startHearbeat(): void {

        _this.countDownTimer();

        _this.hearbeat.interval = setInterval(() => {
          _this.hearbeat.timesLooped++;

          // Check if interval should be stopped
          if (_this.hearbeat.timeout < _this.hearbeat.intervalTimeout * _this.hearbeat.timesLooped) {
            stopHeartbeat();

            console.log('Redirecting to completed but never got the succeeded status');

            _this.$router.push(routesConst.ORDER_COMPLETED)
                .finally(() => {
                  _this.init();
                });
            return;
          }

          if (_this.hearbeat.loading) {
            return;
          }

          triggerHearbeat();

        }, _this.hearbeat.intervalTimeout);

        triggerHearbeat();
      }

      function triggerHearbeat(): void {
        _this.hearbeat.loading = true;

        axios.get(apiService.pageOrderPageOrderPayseraHeartbeatEndpoint() + '?' + buildQuery())
            .then(response => {
              switch (response.status) {
                case 202: // order status: waiting_confirmation (Equivalent to: PaymentStatuses.STATUS_PAYMENT_ORDER_ACCEPTED_BUT_NOT_YET_EXECUTED)
                  break;

                case 200:
                  handleOrderByStatus(response.data.message);
                  stopHeartbeat();
                  break;
              }
              _this.hearbeat.loading = false;
            })
            .catch(error => {
              console.error(error);

              stopHeartbeat();
              _this.hearbeat.loading = false;

              // alert('Something went wrong. Please contact us.');
            });

        function buildQuery(): string {
          const routeQuery = _this.$route.query;

          // Assume that we only process paysera here otherwise we need to know which payment is in progress to generate good hearbeat url
          let query = '';

          const prop = 'hash';
          if (routeQuery.hasOwnProperty(prop)) {
            query += prop + '=' + routeQuery[prop];
          }

          // for (const prop in routeQuery) {
          //   if (! routeQuery.hasOwnProperty(prop)) {
          //     continue;
          //   }
          //   if (query.length > 0) {
          //     query += '&';
          //   }
          //   query += prop + '=' + routeQuery[prop];
          // }

          return query;
        }

        function handleOrderByStatus(status): void {
          switch (status) {
            case PaymentStatuses.STATUS_PAYMENT_WAS_EXECUTED_BUT_CONFIRMATION_WONT_BE_SENT:
            case PaymentStatuses.STATUS_PAYMENT_SUCCESSFUL:
              _this.$router.push(routesConst.ORDER_COMPLETED).finally(() => {
                _this.init();
              });
              break;

            case PaymentStatuses.STATUS_PAYMENT_HAS_NOT_BEEN_EXECUTED:
              _this.$router.push(routesConst.ORDER_FAILED).finally(() => {
                _this.init();
              });
              break;
          }
        }
      }

      function stopHeartbeat(): void {
        clearInterval(_this.hearbeat.interval);
      }
    },

    redirectToCustomerPurchaseHistory(): void {
      clearTimeout(this.countDownId);

      if (! this.store.getters["customer/isLoggedIn"]) {
        console.log('redirect to HOME');
        this.$router.push(routesConst.HOME);
        return;
      }

      console.log('redirect to PROFILE_PURCHASE_HISTORY');

      this.$router.push(routesConst.PROFILE_PURCHASE_HISTORY);
    },

    clearOrderStoreAndLocalStorage(): void {

      // Clear state
      this.store.commit('order/CLEAR_STATE');
      this.store.commit('orderFalseDriver/CLEAR_STATE');
      this.store.commit('orderTrueDriver/CLEAR_STATE');

      // this.store.commit('orderTrueDriverRoute/CLEAR_STATE'); // TODO copy from order clear state (NO NEED)
      this.store.commit('orderTrueDriverDates/CLEAR_STATE'); // TODO copy from order clear state

      this.store.commit('orderTrueRoundTrip/CLEAR_STATE');
      this.store.commit('orderFalseRoundTrip/CLEAR_STATE');
      this.store.commit('vehicle/CLEAR_STATE');

      // INITIALIZE
      this.store.commit('order/INITIALIZE_ORDER_STATE');
      this.store.commit('orderFalseDriver/INITIALIZE_FALSE_DRIVER_ORDER_STATE');
      this.store.commit('orderTrueDriver/INITIALIZE_TRUE_DRIVER_ORDER_STATE');
      this.store.commit('orderTrueRoundTrip/INITIALIZE_TRUE_ROUND_TRIP_STATE');
      this.store.commit('orderFalseRoundTrip/INITIALIZE_FALSE_ROUND_TRIP_STATE');
      this.store.commit('vehicle/INITIALIZE');

      // Ensure Rent service without driver default address if none selected from before
      (new RentService(this.store)).ensureCompanyAddressPreselect(this.store.state.orderFalseDriver);
    }
  },
  beforeCreate(): void {

  },
  created(): void {
    this.init();
  },
})
</script>